import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, Route } from '@angular/router';
import { SidebarService } from '../../service/sidebar.service';
import { EventsService } from '../../service/events.service';
import { Event } from '../../model/event';
import { AdminService } from '../../service/admin.service';
import { SidebarItem, AdminSidebarItem } from '../../model/sidebar-item';
import { EVENT_ADMIN_ICONS } from './event-admin-icons';
import { NavbarService } from '../../service/navbar.service';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { FixedTopProgressBarComponent } from 'src/app/component/fixed-top-progress-bar/fixed-top-progress-bar.component';
import { AuthService } from '../../service/auth.service';
import { UserEventRole } from '../../enum/user.event.role';
import { forkJoin } from 'rxjs';
import { log } from 'console';

@Component({
  selector: 'app-event-admin',
  templateUrl: './event-admin.component.html',
  styleUrls: ['./event-admin.component.scss']
})
export class EventAdminComponent implements OnInit, OnDestroy {
  eventId: number;
  event: Event;
  sidebarItems: Array<SidebarItem>;
  subscriptions: Subscription[] = [];

  // Memória per-evento: última URL visitada dentro de `/admin/{eventId}/...`.
  // Quando o usuário volta ao evento (entra em `/admin/{eventId}` raiz),
  // redirecionamos pra essa URL salva — o evento "abre" onde foi deixado.
  // Persistido a cada NavigationEnd dentro do escopo do evento corrente.
  private static readonly LAST_PAGE_BY_EVENT_KEY = 'jems3-admin-last-page-by-event';

  @ViewChild(FixedTopProgressBarComponent, { static: true }) progressBar: FixedTopProgressBarComponent;

  constructor(
    private sidebarService: SidebarService,
    private navbarService: NavbarService,
    private adminService: AdminService,
    private eventsService: EventsService,
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    private authService: AuthService,
  ) { }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.navbarService.loadingStop();
  }

  updateHeadTitle(){
    // Roda em TODO NavigationEnd — o evento pode ainda não ter chegado (ou o
    // getEvent ter falhado); sem o guard, qualquer navegação nesse estado
    // estoura "Cannot read properties of undefined".
    if (!this.event) { return; }
    let eventAcronymYear = this.event.acronym + " " + this.event.year + " - ";
    let oldTitle = this.titleService.getTitle();
    if (!oldTitle.includes(eventAcronymYear)){
      this.titleService.setTitle(eventAcronymYear + this.titleService.getTitle());
    }
  }

  // ─── Per-event "última página visitada" ──────────────────────────────

  private loadLastPageByEvent(): Record<string, string> {
    try {
      const raw = localStorage.getItem(EventAdminComponent.LAST_PAGE_BY_EVENT_KEY);
      return raw ? (JSON.parse(raw) as Record<string, string>) : {};
    } catch {
      return {};
    }
  }

  private persistLastPageIfWithinEvent(url: string): void {
    if (!this.eventId || !url) { return; }
    const prefix = `/admin/${this.eventId}`;
    // Só persiste URLs dentro do escopo do evento corrente.
    if (url !== prefix && !url.startsWith(prefix + '/')) { return; }
    // Não persiste a raiz `/admin/{eventId}` (não traria info nova no restore).
    if (url === prefix) { return; }
    const map = this.loadLastPageByEvent();
    map[String(this.eventId)] = url;
    try {
      localStorage.setItem(
        EventAdminComponent.LAST_PAGE_BY_EVENT_KEY,
        JSON.stringify(map)
      );
    } catch { /* quota/private mode — silencioso */ }
  }

  private restoreLastPageForEvent(restrictedForPublicationChair: boolean = false): void {
    if (!this.eventId) { return; }
    const currentUrl = (this.router.url || '').split('?')[0].replace(/\/$/, '');
    const eventRoot = `/admin/${this.eventId}`;
    // Só redireciona se o usuário entrou no evento via raiz; navegação
    // direta pra uma sub-página deve respeitar a URL escolhida.
    if (currentUrl !== eventRoot) { return; }
    const saved = this.loadLastPageByEvent()[String(this.eventId)];
    // Publication chair puro: se a última URL visitada é fora do subset
    // permitido (visitada em sessão anterior quando ainda era chair, ou é
    // uma URL restrita agora), pula o restore e cai na home natural do
    // papel — Publicação. Sem isso o publication chair aterrissa num 403
    // sem forma de navegar pra fora dele.
    if (restrictedForPublicationChair) {
      const home = `/admin/${this.eventId}/publication`;
      if (!saved || !this.isUrlAllowedForPublicationChair(saved)) {
        if (currentUrl !== home) { this.router.navigateByUrl(home); }
        return;
      }
    }
    if (!saved || saved === currentUrl) { return; }
    this.router.navigateByUrl(saved);
  }

  /** Whitelist de prefixos que o publication chair pode acessar dentro de
   *  `/admin/{eventId}/**`. Deve casar com o sidebar restrito. */
  private isUrlAllowedForPublicationChair(url: string): boolean {
    const base = `/admin/${this.eventId}`;
    const allowedPrefixes = [
      `${base}/favorites`,
      `${base}/program`,
      `${base}/publication`,
      `${base}/submissions/submission-files`,
      `${base}/submissions/bibtex-export`,
    ];
    return allowedPrefixes.some(p => url === p || url.startsWith(p + '/') || url.startsWith(p + '?'));
  }

  ngOnInit() {
    this.adminService.progressBar = this.progressBar;
    this.navbarService.progressBar = this.adminService.progressBar;

    this.route.params.subscribe(({ eventId }) => {
      this.eventId = Number.parseInt(eventId, 10);

      this.adminService.fetchSelectedEvent(eventId);

      this.eventsService.getEvent(this.eventId).subscribe(event => {
        this.event = event;
        this.updateHeadTitle();
      });

      // Restore da última página é chamado DENTRO do forkJoin de papel
      // (abaixo, no `setTimeout`) — precisamos saber se é publication chair
      // puro pra ignorar URLs salvas fora do seu subset permitido.

      this.subscriptions.push(this.router.events.subscribe(value => {
        if(value instanceof NavigationEnd){
          this.updateHeadTitle();
          this.persistLastPageIfWithinEvent(value.urlAfterRedirects);
        }
      }));

      setTimeout(() => {
        // Publication chair puro (não é chair/admin) recebe um sidebar
        // reduzido: só Publicação + Programa/Sessões + subset read-only
        // de Submissões (ranking/statistics/show-all/downloads/bibtex/
        // certificate-template). Nada de Ajustes, Reviews, Emails, People.
        //
        // O fetch de `this.event` entra no forkJoin pra o build ler o flag
        // `tpcBulkCreateUsersEnable` (gate do item "Lista de autores").
        // Sem isso o build correria antes do event carregar e o item
        // ficaria fora mesmo quando a função estivesse habilitada.
        forkJoin([
          this.authService.userIsAdminOrChair(this.eventId),
          this.authService.directUserIsUserEvent(this.eventId, UserEventRole.PUBLICATION_CHAIR),
          this.eventsService.getEvent(this.eventId),
        ]).subscribe(([isChair, isPubChair, event]) => {
          this.event = event;
          const restricted = !isChair && isPubChair;
          this.sidebarItems = restricted
            ? this.buildRestrictedPublicationChairSidebar()
            : this.buildFullSidebar();
          this.restoreLastPageForEvent(restricted);

          // Rebuild vivo do sidebar quando o `selectedEvent` muda (save
          // do Informações emite via `adminService.selectedEvent = ev`).
          // Sem isso, toggles como `tpcBulkCreateUsersEnable` só aparecem/
          // somem depois de F5. `restricted` já foi resolvido acima; aqui
          // só precisamos do novo `event` pra reler o flag.
          this.subscriptions.push(this.adminService.selectedEventChanges$.subscribe(ev => {
            if (!ev || ev.id !== this.eventId) { return; }
            this.event = ev;
            this.sidebarItems = restricted
              ? this.buildRestrictedPublicationChairSidebar()
              : this.buildFullSidebar();
          }));
        });
      });
    });
  }

  private buildFullSidebar(): Array<SidebarItem> {
    return [
          new SidebarItem(`/admin/${this.eventId}/favorites`, { name: EVENT_ADMIN_ICONS.favorites, font: 'material-symbols-outlined' }, 'menus.sidebar.favorites', {
            noLink: true,
            children: []
          }),
          new SidebarItem(`/admin/${this.eventId}`, { name: EVENT_ADMIN_ICONS.event, font: 'material-symbols-outlined' }, 'menus.sidebar.event', {
            noLink: true,
            children: [
              new AdminSidebarItem('menus.admin.event.dashboard', `/admin/${this.eventId}`, false, EVENT_ADMIN_ICONS.dashboard),
              new AdminSidebarItem('menus.admin.event.checklist', `/admin/${this.eventId}/checklist`, false, EVENT_ADMIN_ICONS.checklist),
              new AdminSidebarItem('menus.admin.settings.event', `/admin/${this.eventId}/settings`, false, EVENT_ADMIN_ICONS.settings),
              new AdminSidebarItem('menus.admin.settings.topics', `/admin/${this.eventId}/settings/topics`, false, EVENT_ADMIN_ICONS.topics),
              new AdminSidebarItem('menus.admin.people.members', `/admin/${this.eventId}/people/members`, false, EVENT_ADMIN_ICONS.people)
            ]
          }),
          new SidebarItem(`/admin/${this.eventId}/submissions`, { name: EVENT_ADMIN_ICONS.submissions, font: 'material-symbols-outlined' }, 'menus.sidebar.submissions', {
            divider: false,
            noLink: true,
            children: [
              new AdminSidebarItem('menus.admin.settings.tracks', `/admin/${this.eventId}/settings/tracks`, false, EVENT_ADMIN_ICONS.tracks),
              // new AdminSidebarItem('menus.sidebar.submissions', `/admin/${this.eventId}/submissions`),
              new AdminSidebarItem('menus.admin.submissions.ranking', `/admin/${this.eventId}/submissions/ranking`, false, EVENT_ADMIN_ICONS.ranking),
              // "Lista de autores" só aparece quando a função extra
              // `tpcBulkCreateUsersEnable` está ligada nas Informações do
              // evento (gated ao superusuário). Vale pra QUALQUER papel,
              // inclusive o próprio superusuário — se a função está off,
              // nem chair nem admin vêem o item nem a página.
              ...(this.event?.tpcBulkCreateUsersEnable ? [
                new AdminSidebarItem('menus.admin.submissions.authors-list', `/admin/${this.eventId}/submissions/authors`, false, EVENT_ADMIN_ICONS.authorsList)
              ] : []),
              // Registro de submissões em lote — mesma regra: só com a flag
              // `tpcBulkCreateSubmissionsEnable` ligada, pra qualquer papel.
              ...(this.event?.tpcBulkCreateSubmissionsEnable ? [
                new AdminSidebarItem('menus.admin.submissions.submissions-upload', `/admin/${this.eventId}/submissions/bulk-upload`, false, EVENT_ADMIN_ICONS.submissionsUpload)
              ] : []),
              // new AdminSidebarItem('menus.admin.submissions.missing-manuscripts', `/admin/${this.eventId}/submissions/missing-manuscripts`),
              // new AdminSidebarItem('menus.admin.submissions.groups', `/admin/${this.eventId}/submissions/groups`),
              new AdminSidebarItem('menus.admin.submissions.submission-form', `/admin/${this.eventId}/submissions/submission/form`, false, EVENT_ADMIN_ICONS.submissionForm),
              new AdminSidebarItem('menus.admin.submissions.notify-authors', `/admin/${this.eventId}/submissions/notify`, false, EVENT_ADMIN_ICONS.notifyAuthors),
              new AdminSidebarItem('menus.admin.submissions.certificate-template', `/admin/${this.eventId}/submissions/certificate-template`, false, EVENT_ADMIN_ICONS.certificateTemplate),
              new AdminSidebarItem('menus.admin.submissions.show-all', `/admin/${this.eventId}/submissions/export`, false, EVENT_ADMIN_ICONS.showAllSubmissions),
              new AdminSidebarItem('menus.admin.submissions.statistics', `/admin/${this.eventId}/submissions/statistics`, false, EVENT_ADMIN_ICONS.submissionStatistics),
              new AdminSidebarItem('menus.admin.submissions.deleted', `/admin/${this.eventId}/submissions/deleted`, false, EVENT_ADMIN_ICONS.deletedSubmissions)
            ]
          }),
          new SidebarItem(`/admin/${this.eventId}/reviews/configuration`, { name: EVENT_ADMIN_ICONS.reviews, font: 'material-symbols-outlined' }, 'menus.sidebar.reviews', {
            divider: false,
            noLink: true,
            children: [
              new AdminSidebarItem('menus.admin.reviews.configuration', `/admin/${this.eventId}/reviews/configuration`, false, EVENT_ADMIN_ICONS.reviewsConfiguration),
              new AdminSidebarItem(
                'menus.admin.reviews.assignment-suggestions',
                `/admin/${this.eventId}/reviews/assignment-suggestions`,
                false,
                EVENT_ADMIN_ICONS.assignmentSuggestions
              ),
              // Atribuição de revisões em lote — só com a flag
              // `tpcBulkAssignReviewsEnable` ligada, pra qualquer papel.
              ...(this.event?.tpcBulkAssignReviewsEnable ? [
                new AdminSidebarItem('menus.admin.reviews.reviews-upload', `/admin/${this.eventId}/reviews/bulk-upload`, false, EVENT_ADMIN_ICONS.reviewsUpload)
              ] : []),
              new AdminSidebarItem('menus.admin.reviews.review-form', `/admin/${this.eventId}/reviews/review/form`, false, EVENT_ADMIN_ICONS.reviewForm),
              new AdminSidebarItem('menus.admin.reviews.notify', `/admin/${this.eventId}/reviews/reviewers/notify`, false, EVENT_ADMIN_ICONS.notifyReviewers),
              new AdminSidebarItem(
                'menus.admin.reports.reviews.statusBySubmission',
                `/admin/${this.eventId}/reviews/reports/statusBySubmission`,
                false,
                EVENT_ADMIN_ICONS.reviewsStatusBySubmission
              ),
              new AdminSidebarItem(
                'menus.admin.reports.reviews.statusByUser',
                `/admin/${this.eventId}/reviews/reports/statusByUser`,
                false,
                EVENT_ADMIN_ICONS.reviewsStatusByUser
              ),
              new AdminSidebarItem('menus.admin.people.members', `/admin/${this.eventId}/people/members`, false, EVENT_ADMIN_ICONS.people)
            ]
          }),
          new SidebarItem(`/admin/${this.eventId}/emails`, { name: EVENT_ADMIN_ICONS.emails, font: 'material-symbols-outlined' }, 'menus.sidebar.emails', {
            divider: false,
            noLink: true,
            children: [
              new AdminSidebarItem('menus.admin.emails.send-email', `/admin/${this.eventId}/emails/send-email`, false, EVENT_ADMIN_ICONS.sendEmail),
              new AdminSidebarItem('menus.admin.emails.emailStatus', `/admin/${this.eventId}/emails/emailStatus`, false, EVENT_ADMIN_ICONS.emailStatus),
              new AdminSidebarItem('menus.admin.emails.emailTemplate', `/admin/${this.eventId}/emails/emailTemplates`, false, EVENT_ADMIN_ICONS.emailTemplate)
            ]
          }),
          new SidebarItem(`/admin/${this.eventId}/program`, { name: EVENT_ADMIN_ICONS.sessions, font: 'material-symbols-outlined' }, 'menus.sidebar.sessions', {
            divider: false,
            noLink: true,
            children: [
              new AdminSidebarItem('menus.admin.sessions.calendar', `/admin/${this.eventId}/program/calendar`, false, EVENT_ADMIN_ICONS.sessionsCalendar),
              new AdminSidebarItem('menus.admin.sessions.program', `/admin/${this.eventId}/program/list`, false, EVENT_ADMIN_ICONS.sessionsProgram)
            ]
          }),
          new SidebarItem(`/admin/${this.eventId}/publication`, { name: EVENT_ADMIN_ICONS.publication, font: 'material-symbols-outlined' }, 'menus.sidebar.publication', {
            divider: false,
            noLink: true,
            children: [
              new AdminSidebarItem('menus.admin.publication.controls', `/admin/${this.eventId}/publication`, false, EVENT_ADMIN_ICONS.publicationControls),
              new AdminSidebarItem('menus.admin.publication.status', `/admin/${this.eventId}/publication/status`, false, EVENT_ADMIN_ICONS.publicationStatus),
              new AdminSidebarItem('menus.admin.publication.proceedings', `/admin/${this.eventId}/publication/proceedings`, false, EVENT_ADMIN_ICONS.publicationProceedings),
              new AdminSidebarItem('menus.admin.submissions.download-submission-files', `/admin/${this.eventId}/submissions/submission-files`, false, EVENT_ADMIN_ICONS.downloadSubmissionFiles),
              new AdminSidebarItem('menus.admin.submissions.bibtex-export', `/admin/${this.eventId}/submissions/bibtex-export`, false, EVENT_ADMIN_ICONS.bibtexExport)
            ]
          })
    ];
  }

  private buildRestrictedPublicationChairSidebar(): Array<SidebarItem> {
    // Publication chair vê apenas: Favoritos, Programa (calendar/program para
    // referência de ordem dos proceedings) e Publicação (controls/status/
    // proceedings + downloads + bibtex). O backend barra o resto por permissão;
    // esconder na UI evita links que "somem" ao clicar.
    return [
      new SidebarItem(`/admin/${this.eventId}/favorites`, { name: EVENT_ADMIN_ICONS.favorites, font: 'material-symbols-outlined' }, 'menus.sidebar.favorites', {
        noLink: true,
        children: []
      }),
      new SidebarItem(`/admin/${this.eventId}/program`, { name: EVENT_ADMIN_ICONS.sessions, font: 'material-symbols-outlined' }, 'menus.sidebar.sessions', {
        divider: false,
        noLink: true,
        children: [
          new AdminSidebarItem('menus.admin.sessions.calendar', `/admin/${this.eventId}/program/calendar`, false, EVENT_ADMIN_ICONS.sessionsCalendar),
          new AdminSidebarItem('menus.admin.sessions.program', `/admin/${this.eventId}/program/list`, false, EVENT_ADMIN_ICONS.sessionsProgram)
        ]
      }),
      new SidebarItem(`/admin/${this.eventId}/publication`, { name: EVENT_ADMIN_ICONS.publication, font: 'material-symbols-outlined' }, 'menus.sidebar.publication', {
        divider: false,
        noLink: true,
        children: [
          new AdminSidebarItem('menus.admin.publication.controls', `/admin/${this.eventId}/publication`, false, EVENT_ADMIN_ICONS.publicationControls),
          new AdminSidebarItem('menus.admin.publication.status', `/admin/${this.eventId}/publication/status`, false, EVENT_ADMIN_ICONS.publicationStatus),
          new AdminSidebarItem('menus.admin.publication.proceedings', `/admin/${this.eventId}/publication/proceedings`, false, EVENT_ADMIN_ICONS.publicationProceedings),
          new AdminSidebarItem('menus.admin.submissions.download-submission-files', `/admin/${this.eventId}/submissions/submission-files`, false, EVENT_ADMIN_ICONS.downloadSubmissionFiles),
          new AdminSidebarItem('menus.admin.submissions.bibtex-export', `/admin/${this.eventId}/submissions/bibtex-export`, false, EVENT_ADMIN_ICONS.bibtexExport)
        ]
      })
    ];
  }
}
